import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useMemo, useState, type ComponentProps } from "react";

import { useDialogModal } from "../../lib/interactive-window";
import { DialogBody } from "./DialogBody";
import { DialogClose } from "./DialogClose";
import { DialogContent } from "./DialogContent";
import { DialogContext } from "./DialogContext";
import { DialogDescription } from "./DialogDescription";
import { DialogFooter } from "./DialogFooter";
import { DialogHeader } from "./DialogHeader";
import { DialogSection } from "./DialogSection";
import { DialogTitle } from "./DialogTitle";
import { DialogTrigger } from "./DialogTrigger";
import type { DialogPlacement } from "./styles";

export type DialogProps = ComponentProps<typeof DialogPrimitive.Root> & {
  /**
   * `viewport` portals the dialog to the document and centers it on screen. `contained` renders it
   * in place, covering and centered on the nearest positioned ancestor (give that ancestor
   * `relative`), and leaves the rest of the page interactive.
   * @default "viewport"
   */
  placement?: DialogPlacement;
  /**
   * Defaults to modal, except while an interactive window (see `InteractiveWindowProvider`) is open
   * so that window stays usable. Ignored for `placement="contained"`, which is never modal: it must
   * not trap focus or hide the page around it.
   */
  modal?: boolean;
};

/**
 * A window layered over the page (or, with `placement="contained"`, over one region of it). Compose
 * `Dialog.Trigger` and `Dialog.Content`; inside the content, stack `Dialog.Header` (holding
 * `Dialog.Title` and `Dialog.Description`), `Dialog.Body` (holding any number of `Dialog.Section`s),
 * and `Dialog.Footer`. The content never exceeds 90% of the viewport height; past that, the body
 * scrolls between a pinned header and footer.
 *
 * @example
 * ```tsx
 * <Dialog>
 *   <Dialog.Trigger asChild>
 *     <Button>Edit profile</Button>
 *   </Dialog.Trigger>
 *   <Dialog.Content divided>
 *     <Dialog.Header>
 *       <Dialog.Title>Edit profile</Dialog.Title>
 *       <Dialog.Description>Changes are saved when you click Save.</Dialog.Description>
 *     </Dialog.Header>
 *     <Dialog.Body>
 *       <Dialog.Section>…</Dialog.Section>
 *     </Dialog.Body>
 *     <Dialog.Footer>
 *       <Dialog.Close asChild>
 *         <Button variant="outline">Cancel</Button>
 *       </Dialog.Close>
 *       <Button>Save</Button>
 *     </Dialog.Footer>
 *   </Dialog.Content>
 * </Dialog>
 * ```
 */
const DialogRoot = ({
  placement = "viewport",
  modal,
  open,
  defaultOpen = false,
  onOpenChange,
  ...props
}: DialogProps) => {
  const shouldBeModal = useDialogModal();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const resolvedOpen = open ?? uncontrolledOpen;

  const handleOpenChange = (nextOpen: boolean) => {
    if (open === undefined) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const context = useMemo(() => ({ placement, open: resolvedOpen }), [placement, resolvedOpen]);

  return (
    <DialogContext.Provider value={context}>
      <DialogPrimitive.Root
        data-slot="dialog"
        modal={placement === "contained" ? false : modal ?? shouldBeModal}
        open={resolvedOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DialogContext.Provider>
  );
};

export const Dialog = Object.assign(DialogRoot, {
  Trigger: DialogTrigger,
  Content: DialogContent,
  Header: DialogHeader,
  Title: DialogTitle,
  Description: DialogDescription,
  Body: DialogBody,
  Section: DialogSection,
  Footer: DialogFooter,
  Close: DialogClose,
});
